/**
 * Prefixed, time sortable identifiers.
 *
 * Format: `{prefix}_{32 lowercase hex}` where the hex is a UUIDv7 with the dashes removed.
 * UUIDv7 puts a 48 bit millisecond timestamp in the leading bits, so ids sort chronologically
 * as plain strings, which is what makes them usable as DynamoDB sort keys.
 */

export const ID_PREFIXES = {
  org: 'org',
  brand: 'brd',
  user: 'usr',
  session: 'ses',
  device: 'dev',
  identity: 'idn',
  interaction: 'int',
  question: 'que',
  option: 'opt',
  outcome: 'out',
  event: 'evt',
  submission: 'sub',
  response: 'rsp',
} as const;

export type IdKind = keyof typeof ID_PREFIXES;
export type Id<K extends IdKind> = `${(typeof ID_PREFIXES)[K]}_${string}`;

const HEX32 = /^[0-9a-f]{32}$/;

/** Monotonic counter so ids minted inside the same millisecond still sort in creation order. */
let lastTimestamp = -1;
let sequence = 0;

function uuidV7Hex(now = Date.now()): string {
  if (now === lastTimestamp) {
    sequence += 1;
    // 12 bits of counter space. Overflowing means more than 4096 ids in one millisecond, at
    // which point borrowing from the next millisecond keeps ordering intact.
    if (sequence > 0xfff) {
      lastTimestamp += 1;
      sequence = 0;
    }
  } else if (now > lastTimestamp) {
    lastTimestamp = now;
    sequence = 0;
  } else {
    // Clock moved backwards. Stay on the last timestamp rather than emit an id that sorts wrong.
    sequence += 1;
    if (sequence > 0xfff) {
      lastTimestamp += 1;
      sequence = 0;
    }
  }

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  const ts = lastTimestamp;
  bytes[0] = Math.floor(ts / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(ts / 2 ** 32) & 0xff;
  bytes[2] = Math.floor(ts / 2 ** 24) & 0xff;
  bytes[3] = Math.floor(ts / 2 ** 16) & 0xff;
  bytes[4] = Math.floor(ts / 2 ** 8) & 0xff;
  bytes[5] = ts & 0xff;

  // Version 7 in the high nibble of byte 6, counter in the remaining 12 bits.
  bytes[6] = 0x70 | ((sequence >> 8) & 0x0f);
  bytes[7] = sequence & 0xff;
  // RFC 4122 variant.
  bytes[8] = 0x80 | (bytes[8]! & 0x3f);

  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return hex;
}

export function newId<K extends IdKind>(kind: K): Id<K> {
  return `${ID_PREFIXES[kind]}_${uuidV7Hex()}` as Id<K>;
}

export function isId<K extends IdKind>(kind: K, value: unknown): value is Id<K> {
  if (typeof value !== 'string') return false;
  const prefix = ID_PREFIXES[kind];
  if (!value.startsWith(prefix + '_')) return false;
  return HEX32.test(value.slice(prefix.length + 1));
}

/** Milliseconds since epoch encoded in the id, or null when the id is malformed. */
export function idTimestamp(value: string): number | null {
  const underscore = value.indexOf('_');
  if (underscore === -1) return null;
  const hex = value.slice(underscore + 1);
  if (!HEX32.test(hex)) return null;
  return Number.parseInt(hex.slice(0, 12), 16);
}
